import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import Tabs, { tabsClasses } from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import { Grid, Chip, Button } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import EditIcon from '@mui/icons-material/Edit';
import { LazyLoadImage } from 'react-lazy-load-image-component';
import Loader from 'src/components/Loader';
import { getActiveCountries, getBlogDetailSections } from 'src/api/api';

function TabPanel(props) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`blog-section-tabpanel-${index}`}
      aria-labelledby={`blog-section-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

TabPanel.propTypes = {
  children: PropTypes.node,
  index: PropTypes.number.isRequired,
  value: PropTypes.number.isRequired
};

function a11yProps(index) {
  return {
    id: `blog-section-tab-${index}`,
    'aria-controls': `blog-section-tabpanel-${index}`
  };
}

export default function BlogDetailSectionsTabs() {
  const [value, setValue] = useState(0);
  const [loading, setLoading] = useState(false);
  const [countries, setCountries] = useState([]);
  const [sections, setSections] = useState([]);

  const handleChange = (event, newValue) => {
    setValue(newValue);
  };

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const [countriesRes, sectionsRes] = await Promise.all([
          getActiveCountries(),
          getBlogDetailSections()
        ]);
        if (countriesRes.success) setCountries(countriesRes.countries || []);
        if (sectionsRes.success) setSections(sectionsRes.data || []);
      } catch (e) {
        setCountries([]);
        setSections([]);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const getSectionForCountry = (countryId) =>
    sections.find((s) => s.countryId === countryId || s.countryId?._id === countryId);

  return (
    <>
      {loading && <Loader />}
      {!loading && (
        <Box sx={{ mt: 2 }}>
          <Box sx={{ width: '100%' }}>
            <Tabs
              value={value}
              onChange={handleChange}
              variant="scrollable"
              scrollButtons
              aria-label="blog detail sections tabs"
              sx={{
                [`& .${tabsClasses.scrollButtons}`]: {
                  '&.Mui-disabled': { opacity: '0.3' }
                }
              }}
            >
              {countries.map((country, index) => (
                <Tab label={country.countryName} {...a11yProps(index)} key={country._id} />
              ))}
            </Tabs>
          </Box>

          {countries.map((country, index) => {
            const section = getSectionForCountry(country._id);
            return (
              <TabPanel value={value} index={index} key={country._id}>
                <Box sx={{ borderRadius: 1, border: '1px solid #929eaa6b' }}>
                  {section ? (
                    <SectionCard section={section} countryId={country._id} />
                  ) : (
                    <Box sx={{ p: 3, textAlign: 'center' }}>
                      <Typography variant="h6" color="text.secondary" mb={2}>
                        No Blog Detail section data for {country.countryName}.
                      </Typography>
                      <Button
                        variant="contained"
                        color="secondary"
                        component={RouterLink}
                        to="/dashboard/blogs-detail-section/add-section"
                      >
                        Add Section
                      </Button>
                    </Box>
                  )}
                </Box>
              </TabPanel>
            );
          })}
        </Box>
      )}
    </>
  );
}

function SectionCard({ section, countryId }) {
  return (
    <Grid container spacing={2} sx={{ p: 2 }}>
      <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'flex-end' }}>
        <Button
          variant="contained"
          color="success"
          component={RouterLink}
          to={`/dashboard/blogs-detail-section/edit-section/${countryId}`}
          startIcon={<EditIcon />}
        >
          Edit
        </Button>
      </Grid>

      {/* Author */}
      <Grid item xs={12}>
        <Chip color="success" variant="outlined" label="Author" sx={{ mb: 1 }} />
        <Grid container alignItems="center" spacing={2}>
          {section.author?.imageSrc && (
            <Grid item>
              <LazyLoadImage
                alt="Author"
                src={`${process.env.REACT_APP_NODE_ROOT}/uploads/images/${section.author.imageSrc}`}
                width={60}
                height={60}
                style={{ borderRadius: '50%', objectFit: 'cover' }}
              />
            </Grid>
          )}
          <Grid item>
            <Typography variant="subtitle1" fontWeight="bold">
              {section.author?.name || '—'}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {section.author?.role || '—'}
            </Typography>
            {section.author?.email && (
              <Typography variant="body2" color="text.secondary">
                {section.author.email}
              </Typography>
            )}
          </Grid>
        </Grid>
      </Grid>

      {/* Boost Growth */}
      <Grid item xs={12}>
        <Chip color="success" variant="outlined" label="Boost Growth" sx={{ mb: 1 }} />
        <Typography variant="body2">
          <strong>Heading:</strong> {section.boostGrowth?.heading || '—'}
        </Typography>
        <Typography variant="body2">
          <strong>Subtitle:</strong> {section.boostGrowth?.subtitle || '—'}
        </Typography>
        <Typography variant="body2">
          <strong>Services:</strong> {section.boostGrowth?.services?.length || 0} item(s)
        </Typography>
      </Grid>

      {/* Trending Services */}
      <Grid item xs={12}>
        <Chip color="success" variant="outlined" label="Trending Services" sx={{ mb: 1 }} />
        <Typography variant="body2">
          <strong>Count:</strong> {section.trendingServices?.length || 0} item(s)
        </Typography>
        {section.trendingServices?.length > 0 && (
          <Box sx={{ mt: 1 }}>
            {section.trendingServices.map((s, i) => (
              <Typography key={i} variant="body2">
                • {s.name} — {s.detail}
              </Typography>
            ))}
          </Box>
        )}
      </Grid>
    </Grid>
  );
}
